import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { OnboardingWizard } from './OnboardingWizard';
import { Loader2 } from 'lucide-react';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

/**
 * Guard component that checks if user needs onboarding.
 * If the user has no tenant memberships, shows the onboarding wizard.
 * Otherwise, renders the children.
 */
export const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const { authenticated, loading: authLoading } = useAuth();
  const { loading: tenantLoading, requiresOnboarding, error } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  // Show loading while auth or tenant context is loading
  if (authLoading || tenantLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-900">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, let ProtectedRoute handle it
  if (!authenticated) {
    return <>{children}</>;
  }

  // If there was an error fetching context, still show onboarding
  // This is a "fail-open" approach for new users
  if (error) {
    console.warn('Error fetching tenant context, showing onboarding:', error);
  }

  // Show onboarding wizard if user needs to create an organization
  if (requiresOnboarding) {
    const handleOnboardingComplete = () => {
      // Navigate to dashboard after onboarding
      navigate('/dashboard', { replace: true });
    };

    return <OnboardingWizard onComplete={handleOnboardingComplete} />;
  }

  // User has tenant(s), render the app
  return <>{children}</>;
};

export default OnboardingGuard;
